import React, { useState } from 'react';
import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Toolbar,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BrandLogo from '@src/components/BrandLogo';

interface CustomAppBarProps {
    darkMode: boolean;
    setDarkMode: (darkMode: boolean) => void;
}

const NAV_ITEMS = [
    { label: 'Registro', to: '/', icon: <PersonAddAltOutlinedIcon /> },
    { label: 'Términos', to: '/terms', icon: <GavelOutlinedIcon /> },
    { label: 'Privacidad', to: '/privacy', icon: <ShieldOutlinedIcon /> },
    { label: 'Eliminar mis datos', to: '/data-deletion', icon: <DeleteOutlineIcon /> }
];

const CustomAppBar: React.FC<CustomAppBarProps> = ({ darkMode, setDarkMode }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { pathname } = useLocation();

    const themeToggle = (
        <Tooltip title={darkMode ? 'Modo claro' : 'Modo oscuro'}>
            <IconButton onClick={() => setDarkMode(!darkMode)} aria-label="Cambiar tema" size="small">
                {darkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
        </Tooltip>
    );

    return (
        <>
            {/* `sticky` en vez de `fixed`: el fixed necesitaba un Toolbar fantasma para
                empujar el contenido y en móvil dejaba la primera línea debajo de la barra. */}
            <AppBar position="sticky">
                <Toolbar sx={{ gap: 1 }}>
                    <Box component={Link} to="/" sx={{ textDecoration: 'none', mr: 'auto' }}>
                        <BrandLogo size={30} showName={!isMobile} />
                    </Box>

                    {isMobile ? (
                        <>
                            {themeToggle}
                            <IconButton onClick={() => setOpen(true)} aria-label="Abrir menú" edge="end">
                                <MenuIcon />
                            </IconButton>
                        </>
                    ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {/* Todos los enlaces van en `text.primary`: con `text.secondary`
                                (#A0A0A0 sobre #1A1A1A) se veían apagados en modo oscuro. El
                                activo se distingue por peso y subrayado, no por color. */}
                            {NAV_ITEMS.map((item) => {
                                const active = pathname === item.to;
                                return (
                                    <Button
                                        key={item.to}
                                        component={Link}
                                        to={item.to}
                                        color="inherit"
                                        sx={{
                                            color: 'text.primary',
                                            opacity: active ? 1 : 0.8,
                                            fontWeight: active ? 700 : 500,
                                            textDecoration: active ? 'underline' : 'none',
                                            textUnderlineOffset: 6,
                                            textDecorationThickness: 2,
                                            '&:hover': { opacity: 1, textDecoration: active ? 'underline' : 'none' }
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                            <Box sx={{ pl: 1 }}>{themeToggle}</Box>
                        </Stack>
                    )}
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: 280 } }}
            >
                <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
                    <BrandLogo size={28} />
                    <IconButton onClick={() => setOpen(false)} sx={{ ml: 'auto' }} aria-label="Cerrar menú">
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Divider />
                <List sx={{ py: 1 }}>
                    {NAV_ITEMS.map((item) => (
                        <ListItemButton
                            key={item.to}
                            component={Link}
                            to={item.to}
                            selected={pathname === item.to}
                            onClick={() => setOpen(false)}
                        >
                            <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>
        </>
    );
};

export default CustomAppBar;
